// ---------------------------------------------------------------------------
// WCAG 2.1 contrast math.
//
// Pure helpers: color parsing, relative luminance, contrast ratio, large-text
// classification, required ratio lookup. No dependencies on slide types.
// ---------------------------------------------------------------------------

export interface Rgba {
  r: number;
  g: number;
  b: number;
  /** 0..1 */
  a: number;
}

const HEX3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RGB_FN = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i;

/** Parse hex (#rgb, #rrggbb), `rgb(…)`, or `rgba(…)`. Returns null on failure. */
export function parseColor(input: string | undefined | null): Rgba | null {
  if (!input) return null;
  const s = input.trim();
  const m3 = s.match(HEX3);
  if (m3) {
    return {
      r: parseInt(m3[1] + m3[1], 16),
      g: parseInt(m3[2] + m3[2], 16),
      b: parseInt(m3[3] + m3[3], 16),
      a: 1,
    };
  }
  const m6 = s.match(HEX6);
  if (m6) {
    return {
      r: parseInt(m6[1], 16),
      g: parseInt(m6[2], 16),
      b: parseInt(m6[3], 16),
      a: 1,
    };
  }
  const mf = s.match(RGB_FN);
  if (mf) {
    const r = clampChannel(parseFloat(mf[1]));
    const g = clampChannel(parseFloat(mf[2]));
    const b = clampChannel(parseFloat(mf[3]));
    const a = mf[4] === undefined ? 1 : clampAlpha(parseFloat(mf[4]));
    if ([r, g, b, a].some(Number.isNaN)) return null;
    return { r, g, b, a };
  }
  if (s.toLowerCase() === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };
  return null;
}

function clampChannel(n: number): number {
  if (Number.isNaN(n)) return NaN;
  return Math.max(0, Math.min(255, Math.round(n)));
}
function clampAlpha(n: number): number {
  if (Number.isNaN(n)) return NaN;
  return Math.max(0, Math.min(1, n));
}

/** True when the color is fully opaque (alpha >= 1). */
export function isOpaque(input: string | undefined | null): boolean {
  const c = parseColor(input);
  return c !== null && c.a >= 1;
}

/** Composite `fg` over `bg`, returning a fully-opaque sRGB color. */
export function compositeOver(fg: Rgba, bg: Rgba): Rgba {
  if (fg.a >= 1) return { ...fg, a: 1 };
  const a = fg.a + bg.a * (1 - fg.a);
  if (a <= 0) return { r: 0, g: 0, b: 0, a: 0 };
  const r = (fg.r * fg.a + bg.r * bg.a * (1 - fg.a)) / a;
  const g = (fg.g * fg.a + bg.g * bg.a * (1 - fg.a)) / a;
  const b = (fg.b * fg.a + bg.b * bg.a * (1 - fg.a)) / a;
  return { r: Math.round(r), g: Math.round(g), b: Math.round(b), a: 1 };
}

/** WCAG relative luminance for an opaque sRGB color. */
export function relativeLuminance(c: Rgba): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
}

/**
 * Contrast ratio between two colors per WCAG 2.1.
 *
 * If `fg` has alpha < 1 it is composited over `bg` before measuring so the
 * computed luminance reflects what the viewer actually sees. Result is rounded
 * down to 2 decimal places (no upward rounding at thresholds).
 */
export function contrastRatio(fg: string, bg: string): number | null {
  const fgRgba = parseColor(fg);
  const bgRgba = parseColor(bg);
  if (!fgRgba || !bgRgba) return null;
  if (bgRgba.a < 1) return null;
  const effectiveFg = fgRgba.a >= 1 ? fgRgba : compositeOver(fgRgba, bgRgba);
  const l1 = relativeLuminance(effectiveFg);
  const l2 = relativeLuminance(bgRgba);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  const ratio = (lighter + 0.05) / (darker + 0.05);
  return Math.floor(ratio * 100) / 100;
}

/**
 * WCAG "large text" classification: >=18pt, OR >=14pt and bold.
 * Bold = numeric weight >=600 OR the string "bold".
 */
export function isLargeText(
  fontSizePt: number | undefined,
  fontWeight: string | undefined
): boolean {
  const size = fontSizePt ?? 0;
  if (size >= 18) return true;
  if (size >= 14 && isBoldWeight(fontWeight)) return true;
  return false;
}

export function isBoldWeight(fontWeight: string | undefined): boolean {
  if (!fontWeight) return false;
  const s = fontWeight.trim().toLowerCase();
  if (s === 'bold' || s === 'bolder') return true;
  const n = Number(s);
  return Number.isFinite(n) && n >= 600;
}

/** Required contrast ratio for a check, per WCAG 2.1 AA. */
export function requiredRatio(opts: { isLargeText: boolean; isNonText: boolean }): number {
  if (opts.isNonText) return 3;
  return opts.isLargeText ? 3 : 4.5;
}

/**
 * Return whichever of black or white gives the best contrast against `bg`.
 * Used by one-click fix suggestions and the auto text-panel color.
 */
export function pickBlackOrWhiteForBackground(bg: string): '#000000' | '#FFFFFF' {
  const ratioBlack = contrastRatio('#000000', bg);
  const ratioWhite = contrastRatio('#FFFFFF', bg);
  if (ratioWhite === null && ratioBlack === null) return '#000000';
  if (ratioBlack === null) return '#FFFFFF';
  if (ratioWhite === null) return '#000000';
  return ratioWhite >= ratioBlack ? '#FFFFFF' : '#000000';
}

/**
 * For a panel that will sit *behind* the given text color, pick black or
 * white — whichever yields better contrast against the text.
 */
export function pickBlackOrWhiteForText(textColor: string): '#000000' | '#FFFFFF' {
  // Contrast is symmetric: the surface that reads best as foreground over this
  // text color is also the surface this text color reads best on.
  return pickBlackOrWhiteForBackground(textColor);
}
